/**
 * S20. Account settings.
 *
 * The same sectioned column as S19, and one section on it matters more than
 * the rest of the screen put together.
 *
 * THE SIMPLE AND ADVANCED SWITCH IS THE CENTREPIECE, because it is not a
 * screen feature, it is the cross cutting mechanic the whole product rests on.
 * It appears twice by design, once in the rail where it is one click from
 * every screen, and once here, where there is finally room to write the promise
 * out in full: Advanced adds identifiers, raw numbers, logs and your app's
 * configuration, and it never hides anything Simple shows. That second
 * sentence is the load bearing one. If Advanced ever removed something, every
 * screen in the product would have to be designed twice and a person would
 * have to learn which mode to be in before they could trust what they read.
 * This is the one place that promise is stated in full, so it is set as the
 * anchor of the page rather than as the third row of a preferences list.
 *
 * EXPORT EVERYTHING IS A SIMPLE SECTION, and that is the same argument in a
 * different place. Advanced holds identifiers and raw numbers. Getting your own
 * data back out is not a technical detail, it is the no lock in promise, and
 * hiding it behind "Show technical details" would mean the only people who
 * ever found it were the people who already knew it existed. The database
 * screen shows Export as a plain button and this screen now agrees with it.
 *
 * NOTIFICATIONS ARE EVENTS, NOT SETTINGS. "Email me when a deploy doesn't
 * work" is a sentence about a thing that will happen. "Deploy failure
 * notifications" is a category name, and a category name makes a person work
 * out for themselves what turning it on would do to their inbox.
 *
 * THE PASSWORD SECTION TELLS THE TRUTH ABOUT A GITHUB ACCOUNT. It does not
 * show three empty password fields to somebody who has never had a password.
 * It says so, and it offers to add one.
 *
 * FAKEABLE STATES, deep linkable per prototype mechanic (a):
 *   ?state=password   the account has a password, so the change form is shown
 *   ?adv=1            the whole page in Advanced, for the side by side artboard
 */

import { PrivacyChoicesLink } from '../../components/patterns/ConsentNotice';
import { useConsentStore } from '../../lib/analytics/consent';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '../../components/primitives/Button';
import { Checkbox } from '../../components/primitives/Checkbox';
import { showToast } from '../../components/primitives/Toast';
import { Field } from '../../components/primitives/Field';

import { AdvancedDisclosure } from '../../components/patterns/AdvancedDisclosure';
import { CopyField } from '../../components/patterns/CopyField';
import { dateLabel } from '../../components/patterns/DeployRow';
import { SectionPanel } from '../../components/patterns/SectionPanel';
import { SegmentedControl } from '../../components/patterns/SegmentedControl';
import { Skeleton } from '../../components/patterns/Skeleton';
import { ThemePicker } from '../../components/patterns/ThemePicker';
import { PlanSummary } from '../../components/patterns/PlanSummary';
import { UsageStrip } from '../../components/patterns/UsageStrip';

import { deleteAccount, getAccount, getUsage, saveCreditsPayPlan, signOut } from '../../lib/api';
import { DestructiveConfirm } from '../../components/patterns/DestructiveConfirm';
import { ApiError } from '../../lib/http';
import { useAccount } from '../../lib/auth/use-app-data';
import { DETAIL_TOGGLE_ENABLED, useAdvanced } from '../../lib/hooks/use-advanced';
import { useAsync } from '../../lib/hooks/use-async';
import { usePrefersStill } from '../../lib/hooks/use-theme';
import { useFlash } from '../../lib/hooks/use-flash';
import { useChooseLanguage } from '../../lib/hooks/use-language';
import { useScreenState } from '../../lib/hooks/use-screen-state';
import { t } from '../../lib/i18n/t';
import { useDemoStore } from '../../lib/mock/store';
import { captureEvent } from '../../lib/analytics/posthog';
import type { DetailLevel } from '../../lib/types';

import '../../styles/components/account.css';

const STATES = ['resting', 'password'] as const;
type StateName = (typeof STATES)[number];

/* Both of these sections describe things the product does not do yet: no
   notification is actually sent by any of the three checkboxes, and there is
   no account-wide export route behind the button. They are written and
   styled, so they are gated rather than deleted; flip either to true when the
   backing route lands. */
const SHOW_NOTIFICATIONS = false;
const SHOW_DATA_EXPORT = false;

interface Alert {
  id: 'deploy' | 'down' | 'limit';
  label: string;
  defaultOn: boolean;
}

/**
 * Each one is phrased as an event, never as a category.
 *
 * Built per render so every label is a literal inside t(). A constant list of
 * bare English strings handed to t(variable) at the render site is invisible
 * to a literal scanning extractor, so it renders in English in every language.
 */
function alertList(): Alert[] {
  return [
    { id: 'deploy', label: t("Email me when a deploy doesn't work"), defaultOn: true },
    { id: 'down', label: t('Email me when one of my apps goes down'), defaultOn: true },
    { id: 'limit', label: t("Email me when I'm close to a limit"), defaultOn: false },
  ];
}

export default function Account() {
  const navigate = useNavigate();
  const consentChoice = useConsentStore((s) => s.choice);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const scenarioId = useDemoStore((s) => s.scenarioId);
  const lang = useDemoStore((s) => s.prefs.lang);
  const chooseLanguage = useChooseLanguage();
  const { state } = useScreenState<StateName>(STATES, 'resting');
  const [advanced, setAdvanced] = useAdvanced();
  // No longer rendered anywhere on this screen: the Motion block that read it
  // is gone. Kept because the screen-view event reports it, and what share of
  // people ask for reduced motion is worth knowing whether or not a section
  // says so back to them.
  const still = usePrefersStill();

  const [savedProfile, flashProfile] = useFlash();
  const [savedAlerts, flashAlerts] = useFlash();
  const [exportAsked, flashExport] = useFlash(4_000);
  const [alerts, setAlerts] = useState<Record<Alert['id'], boolean>>({
    deploy: true,
    down: true,
    limit: false,
  });

  const cachedAccount = useAccount();
  const { data: fetchedAccount, isLoading: isAccountLoading } = useAsync(getAccount, [scenarioId]);
  const account = fetchedAccount ?? cachedAccount;

  const { data: usage, refetch: refetchUsage } = useAsync(() => getUsage(null), [scenarioId]);
  const [savingAutoPay, setSavingAutoPay] = useState(false);

  // Auto-pay (2026-10-13): credits pay the plan at each cycle start unless
  // the owner turns it off. Saved straight away, like the language switch.
  async function setAutoPay(on: boolean) {
    setSavingAutoPay(true);
    captureEvent('account_autopay_changed', { on, plan: usage?.plan ?? null });
    try {
      await saveCreditsPayPlan(on);
      refetchUsage();
    } catch (err) {
      captureEvent('account_autopay_change_failed', { on });
      showToast(t('Could not save that. Try again.'));
      console.error(err);
    } finally {
      setSavingAutoPay(false);
    }
  }

  useEffect(() => {
    if (account) {
      const level: DetailLevel = advanced ? 'advanced' : 'simple';
      captureEvent('account_screen_viewed', {
        detail_level: level,
        language: lang,
        prefers_still: still,
      });
    }
  }, [account, advanced, lang, still]);

  if ((isAccountLoading && !account) || !account) {
    return (
      <div className="page">
        <header className="page__head">
          <h1 className="page__title">{t('Account')}</h1>
        </header>
        <Skeleton shape="panel" count={3} label={t('Loading your account')} />
      </div>
    );
  }

  const level: DetailLevel = advanced ? 'advanced' : 'simple';

  return (
    <div className="page">
      <header className="page__head">
        <h1 className="page__title">{t('Account')}</h1>
      </header>

      <SectionPanel title={t('Profile')}>
        <div className="acct__profile">
          <span className="acct__avatar" aria-hidden="true">
            {account.name.slice(0, 1)}
          </span>
          <p className="acct__avatar-say">
            {t('Your picture comes from GitHub, where you sign in as {handle}.', {
              handle: `@${account.githubHandle}`,
            })}
          </p>
        </div>

        <div className="acct__control">
          <Field label={t('Name')} defaultValue={account.name} />

          {/* Read only, with no Change control at all. The address is the
              identity SV Cloud was granted through Loop SSO or an invite,
              and there is no route that rebinds it, so a Change button here
              could only ever open a form that could not be honoured. Saying
              where the address comes from is the honest version. */}
          <Field
            label={t('Email')}
            value={account.email}
            readOnly
            help={t('This is the address you sign in with. Contact us if it needs to change.')}
          />

          <div className="acct__actions">
            <Button variant="outline" onClick={flashProfile}>
              {t('Save')}
            </Button>
            <span className="acct__saved" role="status" aria-live="polite">
              {savedProfile ? t('Saved') : ''}
            </span>
          </div>
        </div>
      </SectionPanel>

      <SectionPanel title={t('Password')}>
        {state === 'password' ? (
          <div className="acct__control">
            <Field label={t('Current password')} type="password" autoComplete="current-password" />
            <Field label={t('New password')} type="password" autoComplete="new-password" />
            <Field label={t('New password again')} type="password" autoComplete="new-password" />
            <div className="acct__actions">
              <Button variant="outline">{t('Change password')}</Button>
            </div>
          </div>
        ) : (
          <div className="acct__control">
            <p className="acct__say">
              {t("You sign in with GitHub. You don't have a password.")}
            </p>
            <div className="acct__actions">
              <Button variant="outline" onClick={() => navigate('/account?state=password')}>
                {t('Add a password')}
              </Button>
            </div>
          </div>
        )}
      </SectionPanel>

      <SectionPanel
        title={t('Appearance')}
        note={
          DETAIL_TOGGLE_ENABLED
            ? t('How the console looks. How much it tells you is the next section.')
            : t('How the console looks.')
        }
      >
        <div className="acct__prefs">
          <div className="acct__pref">
            <h3 className="acct__pref-title">{t('Theme')}</h3>
            <ThemePicker />
          </div>

          <div className="acct__pref">
            <h3 className="acct__pref-title">{t('Language')}</h3>
            <SegmentedControl<'en' | 'th'>
              label={t('Language')}
              value={lang === 'th' ? 'th' : 'en'}
              options={[
                { value: 'en', label: t('English') },
                { value: 'th', label: t('Thai') },
              ]}
              onChange={(next) => chooseLanguage(next, 'account_page')}
            />
          </div>

          <div className="acct__pref">
            <h3 className="acct__pref-title">{t('Set up your computer')}</h3>
            <Button variant="outline" onClick={() => navigate('/setup')}>
              {t('Show the setup prompt')}
            </Button>
          </div>
        </div>
      </SectionPanel>

      {DETAIL_TOGGLE_ENABLED ? (
      <SectionPanel title={t('Details')}>
        <div className="acct__mode">
          <SegmentedControl<DetailLevel>
            label={t('Level of detail')}
            value={level}
            options={[
              { value: 'simple', label: t('Simple') },
              { value: 'advanced', label: t('Advanced') },
            ]}
            onChange={(next) => {
              captureEvent('detail_level_toggled', {
                source: 'account_page',
                detail_level: next,
              });
              setAdvanced(next === 'advanced');
            }}
            describedBy="acct-mode-say"
          />

          <div className="acct__mode-say" id="acct-mode-say">
            <p className="acct__mode-line">
              {t('Simple shows what happened and what to do next.')}
            </p>
            <p className="acct__mode-line">
              {t(
                "Advanced adds identifiers, raw numbers, logs, and your app's configuration. It never hides anything Simple shows.",
              )}
            </p>
          </div>
        </div>
      </SectionPanel>
      ) : null}

      {SHOW_NOTIFICATIONS ? (
        <SectionPanel title={t('Notifications')}>
          <div className="acct__alerts">
            {alertList().map((alert) => (
              <Checkbox
                key={alert.id}
                label={alert.label}
                checked={alerts[alert.id]}
                onChange={(event) => {
                  setAlerts({ ...alerts, [alert.id]: event.target.checked });
                  flashAlerts();
                }}
              />
            ))}
            <span className="acct__saved" role="status" aria-live="polite">
              {savedAlerts ? t('Saved') : ''}
            </span>
          </div>
        </SectionPanel>
      ) : null}

      <SectionPanel
        title={t('Plan')}
        note={t('These numbers are everything on your account, added up.')}
      >
        <UsageStrip usage={usage ?? undefined} waitingLabel={t('Working out your usage')} />
        {usage ? <PlanSummary usage={usage} /> : null}
        {usage?.billing ? (
          // California auto-renewal law (2026-10-25): this switch is the
          // owner's express consent to auto-renewal, so the plan, price,
          // interval and how to cancel sit right beside it. cloud-api records
          // what was shown (billing_consents) and emails a confirmation.
          <Checkbox
            label={t('Pay my plan from my credits')}
            help={
              usage.billing.creditsPayPlan
                ? t(
                    'On: {plan} renews automatically every month for ${price}, paid from your credits, until you turn this off. We email you before each renewal. Turn it off at any time and your plan ends with its cycle.',
                    { plan: usage.billing.planLabel, price: usage.billing.renewalUsd ?? '0.00' },
                  )
                : t(
                    'Off: nothing renews, and your plan ends with its cycle. Turn this on to renew {plan} automatically every month for ${price}, paid from your credits, until you turn it off.',
                    { plan: usage.billing.planLabel, price: usage.billing.renewalUsd ?? '0.00' },
                  )
            }
            checked={usage.billing.creditsPayPlan}
            disabled={savingAutoPay}
            onChange={(event) => void setAutoPay(event.target.checked)}
          />
        ) : null}
      </SectionPanel>

      <SectionPanel title={t('Privacy')} note={t('What this dashboard measures about you, and your choice about it.')}>
        <div className="acct__control">
          <p className="acct__say">
            {consentChoice === 'granted'
              ? t('You allowed product analytics in this browser.')
              : consentChoice === 'denied'
                ? t('You turned product analytics off in this browser.')
                : t('Product analytics is off in this browser until you choose.')}
          </p>
          <div className="acct__actions">
            <PrivacyChoicesLink className="link-inline" />
          </div>
        </div>
      </SectionPanel>

      {/* A trust feature, and a plain Simple section, because getting your own
          data back out is a right rather than a technical detail. It sat inside
          the Advanced disclosure, which put data portability behind a control
          labelled "Show technical details" and made it something a person had
          to already know to look for. The database screen has always shown its
          Export as a plain button; this now agrees with it. */}
      {SHOW_DATA_EXPORT ? (
        <SectionPanel
          title={t('Your data')}
          note={t('Everything you put into SV Cloud can come back out, whenever you want it.')}
        >
          <div className="acct__control">
            <p className="acct__say">
              {t(
                'Export everything on your account: your projects, your database contents, and your files.',
              )}
            </p>
            <div className="acct__actions">
              <Button variant="outline" onClick={flashExport}>
                {t('Export everything')}
              </Button>
              <span className="acct__saved" role="status" aria-live="polite">
                {exportAsked ? t("We'll email you a link when it's ready.") : ''}
              </span>
            </div>
          </div>
        </SectionPanel>
      ) : null}

      <AdvancedDisclosure>
        <div className="acct__adv">
          <dl className="idents">
            <div className="idents__row">
              <dt className="idents__key">{t('Account ID')}</dt>
              <dd className="idents__value">
                <CopyField label={t('Account ID')} value={account.id} size="inline" />
              </dd>
            </div>
            <div className="idents__row">
              <dt className="idents__key">{t('Joined')}</dt>
              <dd className="idents__value mono">{dateLabel(account.createdAt)}</dd>
            </div>
          </dl>
        </div>
      </AdvancedDisclosure>

      <SectionPanel
        title={t('Sign out')}
        note={t("You'll need to sign in again to reach your projects. Nothing else changes.")}
      >
        <div className="acct__actions">
          <Button
            variant="outline"
            onClick={async () => {
              await signOut();
              navigate('/');
            }}
          >
            {t('Sign out')}
          </Button>
        </div>
      </SectionPanel>

      <SectionPanel
        title={t('Delete account')}
        note={t('Removes your sign in and your contact details. Delete your projects first; this does not delete them.')}
      >
        <div className="acct__actions">
          <Button variant="outline" onClick={() => setDeleteOpen(true)}>
            {t('Delete account')}
          </Button>
          <span className="acct__saved" role="status" aria-live="polite">
            {deleteError ?? ''}
          </span>
        </div>
      </SectionPanel>

      <DestructiveConfirm
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => {
          setDeleteError(null);
          deleteAccount(account.email)
            .then(() => navigate('/'))
            .catch((err: unknown) => {
              setDeleteOpen(false);
              setDeleteError(
                err instanceof ApiError && err.code === 'ACCOUNT_HAS_PROJECTS'
                  ? t('Delete your projects first, then delete the account.')
                  : err instanceof ApiError
                    ? err.message
                    : t('Something went wrong deleting your account.'),
              );
            });
        }}
        title={t('Delete your account?')}
        objectName={account.email}
        confirmLabel={t('Delete account')}
        typeToConfirm
        typePrompt={t('Type your email address to confirm')}
        consequences={[
          { kind: 'loss', text: t('Your sign in, your name and email, your API tokens and your GitHub connection are removed.') },
          { kind: 'survives', text: t('Billing records the law makes us keep stay, without your contact details.') },
          { kind: 'loss', text: t('This cannot be undone. Signing in again later starts a new account.') },
        ]}
      />
    </div>
  );
}
